import React, { useState, useEffect, useCallback } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { login, checkSession, getToken, onAuthChange } from '../services/auth';

/**
 * Wraps the whole application. Nothing renders until the server has confirmed
 * a session, so the dashboard is never briefly visible to a signed-out visitor.
 *
 * This is the front door, not the lock: the API refuses unauthenticated
 * requests on its own, so bypassing this component yields empty screens.
 */
export default function LoginGate({ children }) {
  const [state, setState] = useState(() => (getToken() ? 'checking' : 'locked'));
  const [offline, setOffline] = useState(false);

  const verify = useCallback(async () => {
    if (!getToken()) { setState('locked'); return; }
    setState('checking');
    try {
      const ok = await checkSession();
      setOffline(false);
      setState(ok ? 'open' : 'locked');
    } catch (_) {
      // Could not reach the server. The token may well still be valid, so the
      // user is offered a retry rather than being signed out.
      setOffline(true);
      setState('locked');
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => onAuthChange((signedIn) => {
    setState(signedIn ? 'open' : 'locked');
  }), []);

  if (state === 'checking') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gray-600 animate-pulse">
          Checking session…
        </p>
      </div>
    );
  }

  if (state === 'locked') {
    return <SignIn offline={offline} onRetry={verify} onDone={() => setState('open')} />;
  }

  return children;
}

function SignIn({ onDone, offline, onRetry }) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError('');
    try {
      await login(password);
      setPassword('');
      onDone();
    } catch (err) {
      setError(err.message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 safe-top safe-bottom">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <Lock size={34} className="mb-6" style={{ color: 'var(--gold)' }} />
          <p className="text-[9px] tracking-[0.4em] uppercase mb-3" style={{ color: 'var(--gold)' }}>
            RD Lifestyle Group
          </p>
          <h1 className="text-2xl font-light tracking-[0.12em] uppercase text-center">
            Executive Dashboard
          </h1>
          <p className="text-sm text-gray-500 text-center mt-3 leading-relaxed">
            Private system. Sign in to continue.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="rdi-password"
              className="text-[9px] tracking-[0.25em] uppercase text-gray-500 mb-2 block">
              Password
            </label>
            <div className="relative">
              <input
                id="rdi-password"
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                disabled={busy}
                className="w-full bg-black border border-white/15 px-4 py-3 pr-16 text-sm text-white
                  placeholder-gray-600 outline-none focus:border-white/40 tracking-wide disabled:opacity-50"
                placeholder="Enter password"
              />
              <button type="button" onClick={() => setShow((s) => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] tracking-[0.15em] uppercase text-gray-500 hover:text-white">
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-[11px] text-red-400/90 leading-relaxed">{error}</p>
          )}

          <button type="submit" disabled={busy || !password}
            className="w-full text-[10px] tracking-[0.3em] uppercase font-semibold py-3.5 transition-opacity disabled:opacity-40"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
            {busy ? 'Signing in…' : 'Sign In'}
          </button>

          {offline && (
            <button type="button" onClick={onRetry}
              className="w-full text-[9px] tracking-[0.2em] uppercase text-gray-500 hover:text-white py-2">
              Could not reach the server — retry
            </button>
          )}
        </form>

        <div className="flex items-start gap-2 mt-10 pt-6 border-t border-white/8">
          <ShieldCheck size={12} className="mt-0.5 shrink-0 text-gray-700" />
          <p className="text-[9px] text-gray-700 leading-relaxed">
            Client information on this system is confidential. Sessions expire after 30 days.
          </p>
        </div>
      </div>
    </div>
  );
}
